// Session/auth state backed by `/api/ui/*` (Plan 2-BACKEND.md §4). The SPA never uses `Api-Key`;
// login sets a session cookie + CSRF token (Plan 2A §1 "Auth").
//
// The login form is a small state machine driven by `loginStep`:
//   password -> totp -> (session)     existing accounts with TOTP already enrolled
//   password -> enrol -> (session)    first login for an ADMIN with no TOTP secret yet
// `login()` follows the backend's `enrolTotp` flag (`internal/api/ui/auth.go`) to pick the branch
// automatically — see `docs/build-log/steps/0020-login-totp-enrolment.md`. `LoginPage.vue` only
// needs to render whatever `loginStep` currently is.
import { defineStore } from 'pinia'
import { authApi, type MeResponse } from '@/api/ui'
import { setCsrfToken } from '@/api/http'
import { normalizeAccessData, type AccessType } from '@/api/types'
import { NAV_ITEMS } from '@/constants'

export type LoginStep = 'password' | 'totp' | 'enrol'

/** Every login-flow action (`login`, `verifyTotp`, `beginEnrol`, `confirmEnrol`) answers this same
 * shape so `LoginPage.vue` has one pattern: show `error` on failure, otherwise re-render for the
 * (possibly new) `loginStep`. `expired` marks a pending login the backend no longer recognises —
 * the store has already reset to the `password` step by the time the caller sees it. */
export interface LoginFlowResult {
  ok: boolean
  error?: string
  expired?: boolean
}

/** The exact `error` text every pending-login endpoint answers once the 5-minute window (or the
 * login name) no longer matches a pending record (Plan 2A `/auth/2fa/{verify,enrol,enrol/confirm}`,
 * `internal/api/ui/auth.go`). */
const PENDING_EXPIRED = 'Login again'

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null as MeResponse | null,
    initialized: false,
    loginStep: 'password' as LoginStep,
    pendingLogin: '',
    /** Secret + `otpauth://` URL from `/auth/2fa/enrol`, held only while `loginStep === 'enrol'`. */
    enrolSecret: '',
    enrolOtpauth: '',
  }),
  getters: {
    isAuthenticated: (state) => !!state.user,
    isAdmin: (state) => state.user?.type === 'ADMIN',
    timezone: (state) => state.user?.timezone ?? 'UTC',
  },
  actions: {
    /** Returns the current user's access level for a scoped module ('full_access' when they are
     * ADMIN / have no access_data restrictions), or null when the module is off. */
    moduleAccessType(moduleKey: string): AccessType | null {
      if (!this.user) return null
      if (this.user.type === 'ADMIN') return 'full_access'
      const access = normalizeAccessData(this.user.access_data)
      if (!access.resources.includes(moduleKey)) return null
      return (access[`${moduleKey}_access_type`] as AccessType | undefined) ?? 'full_access'
    },
    moduleEnabled(moduleKey: string): boolean {
      if (!this.user) return false
      if (this.user.type === 'ADMIN') return true
      return normalizeAccessData(this.user.access_data).resources.includes(moduleKey)
    },
    /** Whether the user may create/edit/delete in a module — everything but `read_only`. The API
     * refuses writes for read-only scopes (`auth.Scope.CanWrite`) and, for landings/offers, also
     * the file editor and download: a page's source is never shown to a read-only user. */
    canWrite(moduleKey: string): boolean {
      const type = this.moduleAccessType(moduleKey)
      return type !== null && type !== 'read_only'
    },
    /** Whether the user may open a nav item / route: ADMIN everything, USER only the pages whose
     * resource (any of `modules`) is in their access_data; admin-only pages never. */
    canOpen(item: { modules?: string[]; admin?: boolean }): boolean {
      if (!this.user) return false
      if (this.user.type === 'ADMIN') return true
      if (item.admin) return false
      if (!item.modules || item.modules.length === 0) return true
      return item.modules.some((m) => this.moduleEnabled(m))
    },
    /** First page this user may open (after login / when a route is denied). */
    homePath(): string {
      for (const item of NAV_ITEMS) {
        if (this.canOpen(item)) return item.path
      }
      return '/game'
    },
    async fetchMe(): Promise<boolean> {
      try {
        const me = await authApi.me()
        // The CSRF token lives in memory only. After a reload the session cookie is still valid
        // but the token is gone, and the backend answers 401 to every non-GET without it — which
        // the global handler turned into a forced logout on the first dashboard refresh.
        // `/api/ui/me` returns the session's token, so restore it here.
        if (me.csrfToken) setCsrfToken(me.csrfToken)
        this.user = me
        this.initialized = true
        return true
      } catch {
        this.user = null
        this.initialized = true
        return false
      }
    },
    /** Step 1: login + password. Lands on `loginStep` `'totp'` or `'enrol'` when a second factor
     * is needed, or completes the session directly. */
    async login(login: string, password: string): Promise<LoginFlowResult> {
      const res = await authApi.login(login, password)
      if (!res.ok) return { ok: false, error: res.error }
      if (res.requiresTotp) {
        this.pendingLogin = login
        if (res.enrolTotp) return this.beginEnrol()
        this.loginStep = 'totp'
        return { ok: true }
      }
      await this.completeSession(res.csrfToken)
      return { ok: true }
    },
    /** Verifies a code against an already-enrolled secret (`loginStep === 'totp'`). */
    async verifyTotp(code: string): Promise<LoginFlowResult> {
      const res = await authApi.verifyTotp(this.pendingLogin, code)
      if (!res.ok) return this.pendingFailure(res.error)
      await this.completeSession(res.csrfToken)
      return { ok: true }
    },
    /** Fetches the one-time secret + QR source for first-login enrolment. `login()` calls this
     * automatically when the backend demands enrolment; exposed so the page can retry after the
     * "Login again" bounce, since a retried password login lands back here right away. */
    async beginEnrol(): Promise<LoginFlowResult> {
      const res = await authApi.enrolBegin(this.pendingLogin)
      if (!res.ok) return this.pendingFailure(res.error)
      this.loginStep = 'enrol'
      this.enrolSecret = res.secret
      this.enrolOtpauth = res.otpauth
      return { ok: true }
    },
    /** Confirms the enrolment code (`loginStep === 'enrol'`); on success this stores the secret
     * server-side and opens the session exactly like a normal login (redirect, mustChangePassword —
     * see `LoginPage.vue`'s `onSubmit`, shared with the `totp`/direct-login branches). */
    async confirmEnrol(code: string): Promise<LoginFlowResult> {
      const res = await authApi.enrolConfirm(this.pendingLogin, this.enrolSecret, code)
      if (!res.ok) return this.pendingFailure(res.error)
      await this.completeSession(res.csrfToken)
      return { ok: true }
    },
    /** A wrong code keeps the current step so the user just retries; a pending login the backend no
     * longer recognises (`PENDING_EXPIRED`, 5-minute TTL) resets to the password step instead. */
    pendingFailure(error: string): LoginFlowResult {
      if (error === PENDING_EXPIRED) {
        this.resetLoginFlow()
        return { ok: false, error, expired: true }
      }
      return { ok: false, error }
    },
    resetLoginFlow(): void {
      this.loginStep = 'password'
      this.pendingLogin = ''
      this.enrolSecret = ''
      this.enrolOtpauth = ''
    },
    async completeSession(csrfToken?: string): Promise<void> {
      if (csrfToken) setCsrfToken(csrfToken)
      this.resetLoginFlow()
      await this.fetchMe()
    },
    async logout(): Promise<void> {
      try {
        await authApi.logout()
      } finally {
        this.user = null
        setCsrfToken('')
      }
    },
    /** Called by the 401 handler wired in `main.ts` when a request comes back unauthorized. */
    clearSession(): void {
      this.user = null
      setCsrfToken('')
    },
  },
})
